import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';

const PROMPT_SUGGESTION_DEFINITIONS = [
  {
    id: 'safety-productivity-24h',
    icon: 'triangle-alert',
    labelKey: 'welcome.prompt_suggestions.safety_productivity.label',
    promptKey: 'welcome.prompt_suggestions.safety_productivity.prompt',
    tool: 'safety_productivity',
  },
  {
    id: 'safety-risks-24h',
    icon: 'shield-check',
    labelKey: 'welcome.prompt_suggestions.safety_risks.label',
    promptKey: 'welcome.prompt_suggestions.safety_risks.prompt',
    tool: 'safety',
  },
  {
    id: 'driving-cost-24h',
    icon: 'fuel',
    labelKey: 'welcome.prompt_suggestions.driving_cost.label',
    promptKey: 'welcome.prompt_suggestions.driving_cost.prompt',
    tool: 'fuel',
  },
] as const satisfies ReadonlyArray<{
  icon: IconNames;
  id: string;
  labelKey: string;
  promptKey: string;
  tool: string;
}>;

export type AIPromptSuggestionItem = {
  icon: IconNames;
  id: string;
  label: string;
  prompt: string;
  tool: string;
};

type AIPromptSuggestionsProps = {
  className?: string;
  onSelect: (suggestion: AIPromptSuggestionItem) => void;
};

const AIPromptSuggestions = ({ className, onSelect }: AIPromptSuggestionsProps) => {
  const { t } = useTranslation('ai');

  const suggestions = PROMPT_SUGGESTION_DEFINITIONS.map((suggestion) => ({
    ...suggestion,
    label: t(suggestion.labelKey),
    prompt: t(suggestion.promptKey),
  }));

  return (
    <div className={cn('flex w-full flex-col gap-2.5', className)}>
      {suggestions.map((suggestion) => (
        <button
          key={suggestion.id}
          type='button'
          className='group flex min-h-[66px] w-full cursor-pointer items-start gap-3 rounded-lg border border-gray-5 bg-white px-3 py-3 text-left shadow-sm transition-colors hover:border-primary-5 hover:bg-primary-a1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-8 focus-visible:ring-offset-2'
          onClick={() => onSelect(suggestion)}
        >
          <span
            className='mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary-a2 text-primary-10 transition-colors group-hover:bg-primary-a3'
            aria-hidden='true'
          >
            <Icon name={suggestion.icon} className='size-4' />
          </span>
          <Typography as='span' variant='text-sm-medium' className='min-w-0 flex-1 break-words text-gray-11 leading-5'>
            {suggestion.label}
          </Typography>
        </button>
      ))}
    </div>
  );
};

export default AIPromptSuggestions;
